import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';

import { parseClassnames } from '@incloodsolutions/react-toolkit';

import { CloseIcon } from '@workinance/shared/ui/icons';

type DrawerSize = 'sm' | 'md' | 'lg';
type DrawerSide = 'left' | 'right';

interface IDrawerLayoutComponentProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	description?: string;
	size?: DrawerSize;
	/** Which edge it slides in from. Right by default; left suits navigation, which sits on the left. */
	side?: DrawerSide;
	children: ReactNode;
}

const sizeClasses: Record<DrawerSize, string> = {
	sm: 'max-w-sm',
	md: 'max-w-lg',
	lg: 'max-w-2xl',
};

/**
 * The standard side drawer for all Workinance apps: a ModalLayoutComponent that slides in from the
 * right edge (or the left, with `side`) instead of appearing centered, for flows where a full-height panel reads better than a
 * centered dialog. Same <dialog>-based lifecycle as ModalLayoutComponent (focus trap, Escape and the
 * backdrop come from the browser; closes on Escape, a backdrop click or the header's close button,
 * always through `onClose`), so pick whichever layout fits the content.
 */
export function DrawerLayoutComponent({
	isOpen,
	onClose,
	title,
	description,
	size = 'md',
	side = 'right',
	children,
}: IDrawerLayoutComponentProps) {
	const dialogRef = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const dialogElement = dialogRef.current;
		if (!dialogElement) return;

		if (isOpen && !dialogElement.open) dialogElement.showModal();
		if (!isOpen && dialogElement.open) dialogElement.close();
	}, [isOpen]);

	// The page behind a drawer must not scroll while it is open.
	useEffect(() => {
		if (!isOpen) return;

		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen]);

	const handleDialogClick = (event: MouseEvent<HTMLDialogElement>) => {
		// The dialog element itself is only the click target when the backdrop is clicked.
		if (event.target === dialogRef.current) onClose();
	};

	return (
		<dialog
			ref={dialogRef}
			onClose={onClose}
			onClick={handleDialogClick}
			aria-labelledby="drawer-title"
			className={parseClassnames(
				'fixed inset-y-0 m-0 h-dvh max-h-none w-[calc(100%-2rem)] overflow-y-auto border border-rule bg-panel p-0 text-ink shadow-xl transition-transform duration-300 ease-out backdrop:bg-overlay backdrop:transition-opacity backdrop:duration-300 starting:backdrop:opacity-0',
				{
					'right-0 left-auto rounded-l-[28px] starting:translate-x-full':
						side === 'right',
					'right-auto left-0 rounded-r-[28px] starting:-translate-x-full':
						side === 'left',
				},
				sizeClasses[size],
			)}
		>
			<div className="flex items-start justify-between gap-4 border-b border-rule px-6 py-5">
				<div>
					<h2
						id="drawer-title"
						className="text-2xl font-bold tracking-[-0.03em]"
					>
						{title}
					</h2>
					{description ? <p className="mt-1 text-mute">{description}</p> : null}
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="-mr-2 grid size-10 shrink-0 cursor-pointer place-items-center rounded-full transition-colors hover:bg-brand-soft"
				>
					<CloseIcon className="size-4.5" />
				</button>
			</div>

			<div className="px-6 py-5">{children}</div>
		</dialog>
	);
}
